import { useCallback, useEffect, useState } from 'react'

import { useFocusNodeById, useProcessKey, useSetFocus } from '@please/lrud'
import { scroller } from 'react-scroll'

import { LineTileShape } from '@sportworld/js-sdk/src/constants'

import {
  classicNextRowsOffset,
  classicTilesPerRow,
  limitRowsPerList,
  squareNextRowsOffset,
  squareTilesPerRow,
} from '@/constants/list'
import useStore from '@/store/useStore'
import { TileType } from '@/types/servicesTypes/configTypes'
import { SlideDataType } from '@/types/sliderTypes'
import { isPointerMode, splitTilesIntoGroups } from '@/utils/utils'

import { listControllerProps } from './types'

export const findIndexInList = <T extends { id: string }>(
  list: T[][],
  id: string,
) => {
  const initialValue = {
    rowIndex: 0,
    columnIndex: 0,
  }
  return list.reduce((acc, row, index) => {
    const colIndex = row.findIndex((item) => item.id === id)
    return colIndex > -1 ? { rowIndex: index, columnIndex: colIndex } : acc
  }, initialValue)
}

const getFetchLimit = (
  limitTiles: number,
  tilesPerRow: number,
  nextRowsOffset: number,
) => {
  const overflowTilesCount = limitTiles % tilesPerRow
  const addToFullRow = tilesPerRow - overflowTilesCount
  const offsetTilesCount = nextRowsOffset * tilesPerRow

  return overflowTilesCount
    ? limitTiles + addToFullRow + offsetTilesCount
    : limitTiles + offsetTilesCount
}

export const useListController = ({
  fetchData,
  listContextId,
  isSearchListType,
  searchResultType = 'event',
  activeTileId,
  activeTileIndex,
  activeColumn,
  setActiveColumn,
  tileShape,
  listFocusId,
  scrollOptions,
}: listControllerProps) => {
  const setFocus = useSetFocus()
  const processKey = useProcessKey()
  const focusNode = useFocusNodeById(listFocusId)
  const appConfigLimitTiles = useStore(
    (store) => store.common.appConfig.limitTilesPerLine,
  )
  const [groupedListData, setGroupedListData] =
    useState<Nullable<TileType[][]>>(null)
  const [listData, setListData] = useState<Nullable<TileType[]>>(null)

  const groupedListDataLength = groupedListData?.length || 0

  const isDefaultLineType = tileShape === LineTileShape.ASPECT16x9
  const tilesPerRow = isDefaultLineType ? classicTilesPerRow : squareTilesPerRow

  const nextRowsOffset =
    tileShape === LineTileShape.SQUARE || tileShape === LineTileShape.CIRCLE
      ? squareNextRowsOffset
      : classicNextRowsOffset

  const initialGridPosition = {
    rowIndex: Math.floor(
      (activeTileIndex ?? appConfigLimitTiles - 1) / tilesPerRow,
    ),
    columnIndex: (activeTileIndex ?? appConfigLimitTiles - 1) % tilesPerRow,
  }
  const [lastGridPosition, setLastGridPosition] = useState(initialGridPosition)

  const [next, setNext] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading] = useState(false)

  const limitTiles = activeTileIndex || appConfigLimitTiles
  const fetchLimit = getFetchLimit(limitTiles, tilesPerRow, nextRowsOffset)

  useEffect(() => {
    const controller = new AbortController()
    const abortSignal = controller.signal

    if (listContextId) {
      void fetchListData(!!activeTileId, abortSignal)
    } else {
      setListData(null)
    }

    return () => {
      controller.abort()
    }
  }, [listContextId])

  const onMouseOverTile = (data: SlideDataType) => {
    if (!isPointerMode || !focusNode || !groupedListData) {
      return
    }
    const listTilePosition = findIndexInList(groupedListData, data.id)
    setLastGridPosition(listTilePosition)
  }

  useEffect(() => {
    if (activeColumn === undefined || focusNode?.isFocused) {
      return
    }
    setLastGridPosition((prev) => {
      return {
        rowIndex: prev.rowIndex,
        columnIndex: activeColumn,
      }
    })
  }, [activeColumn])

  useEffect(() => {
    const isFirstRow = lastGridPosition.rowIndex === 0
    const isLastRow = lastGridPosition.rowIndex === totalRows - 1

    if (focusNode?.isFocused && (isFirstRow || isLastRow)) {
      return setActiveColumn?.(lastGridPosition.columnIndex)
    }
  }, [lastGridPosition.columnIndex, lastGridPosition.rowIndex])

  const fetchListData = useCallback(
    async (needUpdateGridPosition = false, abortSignal?: AbortSignal) => {
      setLoading(true)
      try {
        const listDataLength = listData?.length || 0
        const isRowLimitExceeded =
          Math.ceil(listDataLength / tilesPerRow) >= limitRowsPerList

        if (listData && isRowLimitExceeded) {
          return setLoading(false)
        }

        const lineData = await fetchData(fetchLimit, next, abortSignal)

        if (!lineData) {
          return setLoading(false)
        }

        if (lineData.items.length < fetchLimit) {
          lineData.next = ''
        }

        const listDataUpd = listData
          ? [...listData, ...lineData.items]
          : lineData.items

        const maxListLength = limitRowsPerList * tilesPerRow

        if (listDataUpd.length > maxListLength) {
          listDataUpd.length = maxListLength
        }

        const rowsCount = Math.ceil(lineData.totalCount / tilesPerRow)
        setTotalRows(
          rowsCount < limitRowsPerList ? rowsCount : limitRowsPerList,
        )

        const groupedData = splitTilesIntoGroups(tilesPerRow, listDataUpd)
        if (needUpdateGridPosition && activeTileId) {
          const activeListTilePosition = findIndexInList(
            groupedData,
            activeTileId,
          )
          setLastGridPosition(activeListTilePosition)
        }
        setGroupedListData(groupedData)
        setNext(lineData.next)

        setListData(listDataUpd)
        setLoading(false)
      } catch (e) {
        console.error('Error: lines data backend response')
      }
    },
    [
      isSearchListType,
      searchResultType,
      loading,
      next,
      listData,
      activeTileId,
      fetchLimit,
      fetchData,
    ],
  )

  const onGridMove = (e: GridMoveEvent) => {
    const nextRowIndex = e.nextRowIndex
    const isDownKey = e.arrow === 'down'
    const needFetchListData =
      nextRowIndex + nextRowsOffset >= groupedListDataLength
    if (!loading && isDownKey && needFetchListData && next) {
      void fetchListData()
    }
    const nextFocusId = `${listFocusId}_row_${nextRowIndex}`

    let columnIndex = e.nextColumnIndex
    const isLastRow = nextRowIndex === totalRows - 1

    if (isLastRow && listData) {
      const itemsInLastRow = listData.length % tilesPerRow || tilesPerRow
      columnIndex = Math.min(columnIndex, itemsInLastRow - 1)
    }

    setLastGridPosition({
      rowIndex: nextRowIndex,
      columnIndex: columnIndex,
    })
    scroller.scrollTo(nextFocusId, scrollOptions)
  }

  const onRightMove = (e: LRUDEvent) => {
    if (e.node.defaultFocusRow === groupedListDataLength - 1 && listData) {
      const itemsInLastRow = listData.length % tilesPerRow || tilesPerRow
      const columnIndex = e.node.defaultFocusColumn
      if (columnIndex + 1 >= itemsInLastRow) {
        e.preventDefault()
        setLastGridPosition((prev) => {
          return {
            rowIndex: prev.rowIndex,
            columnIndex: 0,
          }
        })
        setActiveColumn?.(0)
        setTimeout(processKey.down, 0)
        return
      }
    }

    if (e.node.defaultFocusColumn === tilesPerRow - 1) {
      const nextRowIndex = e.node.defaultFocusRow + 1
      const nextFocusRowId = e.node.children[nextRowIndex]
      if (!nextFocusRowId) {
        return
      }
      setLastGridPosition({
        rowIndex: nextRowIndex,
        columnIndex: 0,
      })
      setTimeout(() => {
        setFocus(nextFocusRowId)
        scroller.scrollTo(nextFocusRowId, scrollOptions)
      }, 0)

      const needFetchListData =
        nextRowIndex + nextRowsOffset >= groupedListDataLength
      if (!loading && needFetchListData && next) {
        void fetchListData()
      }
    }
  }

  return {
    loading,
    groupedListData,
    listData,
    totalRows,
    lastGridPosition,
    onGridMove,
    onRightMove,
    onMouseOverTile,
  }
}
